/**
 * useJobTracking Hook
 * Manages AI content generation job state with localStorage persistence and API sync
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  generateService,
  isJobTerminal,
  isJobRunning,
  type GenerateContentRequest,
  type GenerateContentFromDocumentRequest,
  type RegenerateContentRequest,
  type JobDetail,
} from '@ai-content-hub/shared-api';
import type { UseJobTrackingOptions, UseJobTrackingReturn } from './AIContentGenerator.types';

const DEFAULT_STORAGE_KEY = 'ai-job-tracking';
const DEFAULT_POLLING_INTERVAL = 2000;
const DEFAULT_MAX_POLLING_ATTEMPTS = 150; // 5 minutes at 2s interval

export function useJobTracking(options: UseJobTrackingOptions = {}): UseJobTrackingReturn {
  const {
    storageKey = DEFAULT_STORAGE_KEY,
    pollingInterval = DEFAULT_POLLING_INTERVAL,
    maxPollingAttempts = DEFAULT_MAX_POLLING_ATTEMPTS,
    contentId,
    onJobCompleted,
    onJobFailed,
  } = options;

  // Whether in regeneration mode (contentId provided)
  const isRegeneration = Boolean(contentId);

  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobDetail | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for cleanup and avoiding stale closures
  const pollingControllerRef = useRef<ReturnType<typeof generateService.createPollingController> | null>(null);
  const mountedRef = useRef(true);
  // Track if this is an initial load from storage (don't trigger callbacks for already-completed jobs)
  const isInitialLoadRef = useRef(true);

  // Load job from localStorage on mount
  // If job is already completed/failed/cancelled, clear it for a fresh start
  useEffect(() => {
    mountedRef.current = true;
    isInitialLoadRef.current = true;
    const savedJobId = localStorage.getItem(storageKey);

    if (savedJobId) {
      // Check if we should load or clear the saved job
      syncJobStatusInternal(savedJobId, true).then((status) => {
        // If job is terminal (completed/failed/cancelled), clear it for fresh start
        if (status && isJobTerminal(status.status)) {
          localStorage.removeItem(storageKey);
          setJobId(null);
          setJobStatus(null);
        }
      });
    }

    return () => {
      mountedRef.current = false;
      // Cancel polling on unmount
      if (pollingControllerRef.current) {
        pollingControllerRef.current.cancel();
      }
    };
  }, [storageKey]);

  // Internal function to sync job status
  // isFromStorage: true when loading from localStorage on mount (don't trigger callbacks for completed)
  // Returns the job status for caller to use
  const syncJobStatusInternal = async (id: string, isFromStorage = false): Promise<JobDetail | null> => {
    try {
      const status = await generateService.getJobStatus(id);
      if (!mountedRef.current) return null;

      setJobStatus(status);
      setError(null);

      // If job is still running, start polling (will trigger callbacks when done)
      if (isJobRunning(status.status)) {
        isInitialLoadRef.current = false;
        setJobId(id);
        startPollingInternal(id);
      } else if (isJobTerminal(status.status)) {
        // Job already completed - only trigger callbacks if NOT from initial storage load
        // This prevents the loop when switching back to AI mode
        if (!isFromStorage) {
          if (status.status === 'completed') {
            onJobCompleted?.(status);
          } else if (status.status === 'failed') {
            onJobFailed?.(status.error || 'Job failed');
          }
        }
        // Mark initial load as done
        isInitialLoadRef.current = false;
      }

      return status;
    } catch (err) {
      if (!mountedRef.current) return null;
      const errorMsg = err instanceof Error ? err.message : 'Failed to sync job status';
      setError(errorMsg);
      console.error('Failed to sync job status:', err);
      // If we can't fetch the job, clear it from storage
      localStorage.removeItem(storageKey);
      return null;
    }
  };

  // Internal function to start polling
  const startPollingInternal = (id: string) => {
    // Cancel any existing polling
    if (pollingControllerRef.current) {
      pollingControllerRef.current.cancel();
    }

    setIsPolling(true);

    const controller = generateService.createPollingController(
      id,
      (status) => {
        if (!mountedRef.current) return;

        setJobStatus(status);

        if (isJobTerminal(status.status)) {
          setIsPolling(false);
          setIsGenerating(false);

          if (status.status === 'completed') {
            onJobCompleted?.(status);
          } else if (status.status === 'failed') {
            onJobFailed?.(status.error || 'Job failed');
            setError(status.error || 'Job failed');
          }
        }
      },
      {
        interval: pollingInterval,
        maxAttempts: maxPollingAttempts,
      }
    );

    pollingControllerRef.current = controller;
    controller.start();
  };

  // Start generation (new content)
  const startGeneration = useCallback(
    async (request: GenerateContentRequest) => {
      setIsGenerating(true);
      setError(null);
      setJobStatus(null);

      try {
        const response = await generateService.generateContent(request);
        if (!mountedRef.current) return;

        const newJobId = response.job_id;

        // Save to localStorage
        localStorage.setItem(storageKey, newJobId);
        setJobId(newJobId);

        // Start polling
        startPollingInternal(newJobId);
      } catch (err) {
        if (!mountedRef.current) return;
        const errorMsg = err instanceof Error ? err.message : 'Failed to start generation';
        setError(errorMsg);
        setIsGenerating(false);
        onJobFailed?.(errorMsg);
      }
    },
    [storageKey, onJobFailed, pollingInterval, maxPollingAttempts]
  );

  const startGenerationFromDocument = useCallback(
    async (request: GenerateContentFromDocumentRequest) => {
      setIsGenerating(true);
      setError(null);
      setJobStatus(null);

      try {
        const response = await generateService.generateContentFromDocument(request);
        if (!mountedRef.current) return;

        const newJobId = response.job_id;
        localStorage.setItem(storageKey, newJobId);
        setJobId(newJobId);
        startPollingInternal(newJobId);
      } catch (err) {
        if (!mountedRef.current) return;
        const errorMsg =
          err instanceof Error ? err.message : 'Failed to start document generation';
        setError(errorMsg);
        setIsGenerating(false);
        onJobFailed?.(errorMsg);
      }
    },
    [storageKey, onJobFailed, pollingInterval, maxPollingAttempts]
  );

  // Start regeneration (update existing content)
  const startRegeneration = useCallback(
    async (request: RegenerateContentRequest) => {
      if (!contentId) {
        const errorMsg = 'Content ID is required for regeneration';
        setError(errorMsg);
        onJobFailed?.(errorMsg);
        return;
      }

      setIsGenerating(true);
      setError(null);
      setJobStatus(null);

      try {
        const response = await generateService.regenerateContent(contentId, request);
        if (!mountedRef.current) return;

        const newJobId = response.job_id;

        // Save to localStorage
        localStorage.setItem(storageKey, newJobId);
        setJobId(newJobId);

        // Start polling
        startPollingInternal(newJobId);
      } catch (err) {
        if (!mountedRef.current) return;
        const errorMsg = err instanceof Error ? err.message : 'Failed to start regeneration';
        setError(errorMsg);
        setIsGenerating(false);
        onJobFailed?.(errorMsg);
      }
    },
    [storageKey, contentId, onJobFailed, pollingInterval, maxPollingAttempts]
  );

  // Start tracking an existing job (e.g. from chat regeneration)
  const trackJob = useCallback(
    (id: string) => {
      setIsGenerating(true);
      setError(null);
      localStorage.setItem(storageKey, id);
      setJobId(id);
      syncJobStatusInternal(id);
    },
    [storageKey],
  );

  // Manual sync from API
  const syncJobStatus = useCallback(async () => {
    if (!jobId) return;
    await syncJobStatusInternal(jobId);
  }, [jobId]);

  // Clear job and localStorage
  const clearJob = useCallback(() => {
    localStorage.removeItem(storageKey);
    setJobId(null);
    setJobStatus(null);
    setIsGenerating(false);
    setIsPolling(false);
    setError(null);

    if (pollingControllerRef.current) {
      pollingControllerRef.current.cancel();
      pollingControllerRef.current = null;
    }
  }, [storageKey]);

  // Cancel polling
  const cancelPolling = useCallback(() => {
    if (pollingControllerRef.current) {
      pollingControllerRef.current.cancel();
      pollingControllerRef.current = null;
    }
    setIsPolling(false);
  }, []);

  return {
    jobId,
    jobStatus,
    isGenerating,
    isPolling,
    error,
    isRegeneration,
    startGeneration,
    startGenerationFromDocument,
    startRegeneration,
    trackJob,
    syncJobStatus,
    clearJob,
    cancelPolling,
  };
}

export default useJobTracking;
